import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Edit, Trash2, UserPlus } from "lucide-react";
import { getRequest, deleteRequest } from "../../../../service/index"
import ShiftModal from "../../../../modals/Addshift";
import EditShiftModal from '../../../../modals/editshift';
import ModalWrapper from "../../../../Modal";
import { getEmployeeData } from "../../../../utils/localStorage";

const ShiftTime = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [editShiftOpen, setEditShiftOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);

  // Permission checks
  const employeeData = getEmployeeData();
  const userRole = employeeData?.role;
  const shiftPermissions = employeeData?.designation?.permissions?.Attendance?.['Shift Time'] || [];

  const canView = userRole === "companyadmin" || shiftPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || shiftPermissions.includes("add");
  const canEdit = userRole === "companyadmin" || shiftPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || shiftPermissions.includes("delete");

  useEffect(() => {
    fetchShifts();
    // eslint-disable-next-line
  }, [canView]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      if (!canView) {
        setShifts([]);
      } else {
        const res = await getRequest('/api/companies/shifts');
        if (res && Array.isArray(res.shifts)) {
          setShifts(res.shifts.map(shift => ({
            ...shift,
            id: shift._id,
            title: shift.shiftName,
            // Only use first 3 chars of each day
            days: Array.isArray(shift.workingDays)
              ? shift.workingDays.map(day => day.slice(0, 3)).join(', ')
              : '',
            startTime: shift.startTime,
            endTime: shift.endTime,
            totalHours: shift.workingHours,
            employeeCount: typeof shift.employeeCount === 'number' ? shift.employeeCount : 0,
          })));
        } else {
          setShifts([]);
        }
      }
    } catch (error) {
      setShifts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenShift = () => setShiftOpen(true);
  const handleCloseShift = () => {
    setShiftOpen(false);
    fetchShifts();
  };

  const handleEditShift = (shift) => {
    if (!canEdit) {
      message.warning("You don't have permission to edit shifts.");
      return;
    }
    setSelectedShift(shift);
    setEditShiftOpen(true);
  };
  const handleCloseEditShift = () => {
    setEditShiftOpen(false);
    setSelectedShift(null);
    fetchShifts();
  };

  const handleDeleteShift = (shift) => {
    if (!canDelete) {
      message.warning("You don't have permission to delete shifts.");
      return;
    }

    Modal.confirm({
      title: 'Are you sure you want to delete this shift?',
      content: `This will permanently delete the shift "${shift.title}" and all its associated data.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        try {
          const response = await deleteRequest(`/api/companies/shifts?shiftId=${shift.id}`);
          if (response.message === "Shift deleted successfully") {
            message.success('Shift deleted successfully');
            fetchShifts();
          } else {
            message.error(response.message || 'Failed to delete shift');
          }
        } catch (error) {
          message.error('Failed to delete shift');
        }
      },
    });
  };

  // Helper to format time in 12-hour format with AM/PM
  const formatTime12Hour = (timeStr) => {
    if (!timeStr || timeStr === "-" || timeStr === "ss") return "-";
    const [hours, minutes] = timeStr.split(":");
    let hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (startTime) => formatTime12Hour(startTime),
    },
    {
      title: 'End Time',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (endTime) => formatTime12Hour(endTime),
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
    },
    {
      title: 'No of Employee',
      dataIndex: 'employeeCount',
      key: 'employeeCount',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<Edit className="w-4 h-4" />} 
            onClick={() => handleEditShift(record)}
            className={canEdit ? "text-blue-600 hover:text-blue-800" : "text-gray-400 cursor-not-allowed"}
            disabled={!canEdit}
            title={canEdit ? "Edit" : "You don't have permission to edit"}
          />
          <Button 
            type="text" 
            icon={<Trash2 className="w-4 h-4" />} 
            onClick={() => handleDeleteShift(record)}
            className={canDelete ? "text-red-600 hover:text-red-800" : "text-gray-400 cursor-not-allowed"}
            disabled={!canDelete}
            title={canDelete ? "Delete" : "You don't have permission to delete"}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold">Shift Time</h1>
          <div className='flex gap-2'>
            <Button
              className='bg-primary text-white'
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canAdd}
              onClick={() => canAdd ? handleOpenShift() : message.warning("You don't have permission to add shifts.")}
            >
              Add Shift Time
            </Button>
            {/* <Button className='bg-primary' type="primary" icon={<UserPlus className="w-4 h-4" />}>
              Assign Shift
            </Button> */}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={canView ? shifts : []}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          loading={loading}
          pagination={{
            total: canView ? shifts.length : 0,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
        {!canView && (
          <div className="text-center text-gray-500 py-4">You don't have permission to view shifts.</div>
        )}
      </div>
      <ModalWrapper
        open={shiftOpen}
        onRequestClose={handleCloseShift}
        title="add shift"
      >
        <ShiftModal onClose={handleCloseShift} />
      </ModalWrapper>
      <ModalWrapper
        open={editShiftOpen}
        onRequestClose={handleCloseEditShift}
        title="Edit Shift"
      >
        <EditShiftModal
          open={editShiftOpen} 
          onClose={handleCloseEditShift}
          shift={selectedShift}
        />
      </ModalWrapper>
    </>
  );
}

export default ShiftTime;
